<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import { es } from 'date-fns/locale';
import Button from 'dashboard/components-next/button/Button.vue';
import { taskDotClass, HOVER_REVEAL_CLASS } from '../../leads/helper';

const props = defineProps({
  task: { type: Object, required: true },
  typeName: { type: String, default: '' },
  assigneeName: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  // Oculta el enlace al lead (usar cuando el row ya está dentro de la ficha del lead).
  showLeadLink: { type: Boolean, default: true },
});
const emit = defineEmits(['edit', 'complete', 'remove']);

const route = useRoute();
const { t } = useI18n();

const isCompleted = computed(() => !!props.task.completed_at);
const isOverdue = computed(
  () =>
    !isCompleted.value &&
    !!props.task.due_at &&
    new Date(props.task.due_at) < new Date()
);

const dueLabel = computed(() => {
  if (!props.task.due_at) return '';
  try {
    return format(new Date(props.task.due_at), 'dd MMM HH:mm', { locale: es });
  } catch (error) {
    return '';
  }
});

const leadLink = computed(() => ({
  name: 'leads_show',
  params: { accountId: route.params.accountId, leadId: props.task.lead_id },
}));
</script>

<template>
  <li class="flex items-center gap-2.5 p-2.5 rounded-lg group bg-n-alpha-1">
    <span
      class="size-2 rounded-full shrink-0"
      :class="taskDotClass(task.due_at)"
    />
    <div class="flex flex-col min-w-0 gap-0.5 flex-1">
      <div class="flex items-center min-w-0 gap-2">
        <span
          class="text-sm truncate"
          :class="
            isCompleted ? 'line-through text-n-slate-10' : 'text-n-slate-12'
          "
        >
          {{ task.title }}
        </span>
        <span
          v-if="typeName"
          class="inline-flex items-center flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-n-alpha-2 text-n-slate-11"
        >
          {{ typeName }}
        </span>
      </div>
      <div class="flex items-center gap-2 text-xs">
        <span :class="isOverdue ? 'text-n-ruby-11' : 'text-n-slate-10'">{{
          dueLabel
        }}</span>
        <router-link
          v-if="showLeadLink && task.lead"
          :to="leadLink"
          class="truncate text-n-blue-11 hover:underline"
        >
          {{ task.lead.name }}
        </router-link>
        <span
          v-if="showAssignee && assigneeName"
          class="truncate text-n-slate-10"
        >
          · {{ assigneeName }}
        </span>
      </div>
    </div>
    <!-- Receta hover compartida (HOVER_REVEAL_CLASS en leads/helper.js). -->
    <div
      class="flex items-center gap-0.5 ml-auto transition-opacity"
      :class="HOVER_REVEAL_CLASS"
    >
      <Button
        v-if="!isCompleted"
        ghost
        slate
        sm
        icon="i-lucide-check"
        :title="t('LEADS.TASK.COMPLETE')"
        @click="emit('complete', task)"
      />
      <Button
        ghost
        slate
        sm
        icon="i-lucide-pencil"
        :title="t('LEADS.TASK.EDIT')"
        @click="emit('edit', task)"
      />
      <Button
        ghost
        ruby
        sm
        icon="i-lucide-trash-2"
        :title="t('LEADS.TASK.DELETE')"
        @click="emit('remove', task)"
      />
    </div>
  </li>
</template>
